import { useState, useEffect } from 'react';
import { companyName, companyDetails } from '../configs';
import { authFetch, authFetchRequest } from '../utils/api';
import { UrlDev } from '../url';
import { getCookie } from '../utils/cookies';
import { useCompany } from '../contexts/CompanyContext';
import { alertC } from '../components/dialogService';
import './LogoHeader.css';

const LogoHeader = ({ dealerId, dealerName }) => {
  const { refresh: refreshCompanyContext } = useCompany();
  const [logo, setLogo] = useState('/assets/company_logo.png');
  const [logoFile, setLogoFile] = useState(null);
  const [companyData, setCompanyData] = useState({
    name: companyName || '',
    details1: companyDetails?.details1 || '',
    details2: companyDetails?.details2 || '',
    details3: companyDetails?.details3 || '',
    timezone: ''
  });
  // For the dealer scope we need to know whether the loaded value was an explicit
  // dealer override or merged-in owner fallback, so the dropdown can default to
  // "Use owner's timezone" when nothing is set yet on the dealer.
  const [hasDealerTimezone, setHasDealerTimezone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null); // Store original data for reset

  const dealerQs = dealerId ? `?dealer_id=${dealerId}` : '';

  // Load company data from backend on component mount
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const data = await authFetch(`/company/${dealerQs}`);

        if (data) {
          // For dealer scope we want to show only the dealer's OWN timezone in the
          // dropdown (or "" meaning "Use owner's timezone"). For owner scope we use
          // the timezone field directly.
          const tz = dealerId
            ? (data.dealer_timezone || '')
            : (data.timezone || '');
          const newData = {
            name: data.name || '',
            details1: data.details1 || '',
            details2: data.details2 || '',
            details3: data.details3 || '',
            timezone: tz,
          };
          setCompanyData(newData);
          setOriginalData(newData);
          setHasDealerTimezone(Boolean(data.dealer_timezone));

          // Set logo path if available
          if (data.logo_path) {
            setLogo(UrlDev + data.logo_path);
          }
        }
      } catch (error) {
        console.error('Error loading company data:', error);
        // Fallback to config values on error
        const fallbackData = {
          name: companyName || '',
          details1: companyDetails?.details1 || '',
          details2: companyDetails?.details2 || '',
          details3: companyDetails?.details3 || '',
          timezone: ''
        };
        setCompanyData(fallbackData);
        setOriginalData(fallbackData);
      }
    };

    fetchCompanyData();
  }, [dealerId]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // 1. Upload logo if a new one was selected
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);

        const token = getCookie("access_token");
        const csrfToken = localStorage.getItem("csrf_token");

        if (!token) {
          throw new Error('No authentication token found');
        }

        if (!csrfToken) {
          throw new Error('No CSRF token found');
        }

        const logoResponse = await fetch(`${UrlDev}/company/upload-logo${dealerQs}`, {
          method: 'POST',
          headers: {
            "Authorization": `Bearer ${token}`,
            "X-CSRF-TOKEN": csrfToken,
          },
          credentials: 'include',
          body: formData
        });

        if (!logoResponse.ok) {
          const errorText = await logoResponse.text();
          throw new Error(`Failed to upload logo: ${errorText}`);
        }

        const logoData = await logoResponse.json();
        console.log('Logo uploaded:', logoData.logo_path);

        // Update the logo display with the server path
        setLogo(UrlDev + logoData.logo_path);
        setLogoFile(null); // Clear the file selection
      }

      // 2. Update company data
      const response = await authFetchRequest(`/company/${dealerQs}`, companyData, 'PUT');

      if (response && response.company) {
        console.log('Company data updated:', response.company);

        const savedTz = dealerId
          ? (response.company.dealer_timezone || '')
          : (response.company.timezone || '');
        // Update original data to reflect saved state
        setOriginalData({
          name: response.company.name || '',
          details1: response.company.details1 || '',
          details2: response.company.details2 || '',
          details3: response.company.details3 || '',
          timezone: savedTz,
        });
        setHasDealerTimezone(Boolean(response.company.dealer_timezone));

        // Refresh the global company context so all pages get the new logo
        // (only when editing the current user's own scope; an owner editing
        //  another dealer's branding shouldn't affect their own header)
        if (!dealerId) {
          refreshCompanyContext();
        }

        await alertC('Settings saved successfully! Please refresh your browser (Ctrl+Shift+R or Cmd+Shift+R) to see the new logo on all pages.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      await alertC(`Failed to save settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      // Reload from backend
      const data = await authFetch(`/company/${dealerQs}`);

      if (data) {
        const tz = dealerId ? (data.dealer_timezone || '') : (data.timezone || '');
        const resetData = {
          name: data.name || '',
          details1: data.details1 || '',
          details2: data.details2 || '',
          details3: data.details3 || '',
          timezone: tz,
        };
        setCompanyData(resetData);
        setOriginalData(resetData);
        setHasDealerTimezone(Boolean(data.dealer_timezone));

        if (data.logo_path) {
          setLogo(UrlDev + data.logo_path);
        } else {
          setLogo('/assets/company_logo.png');
        }
      }

      setLogoFile(null);
    } catch (error) {
      console.error('Error resetting data:', error);
      // Fallback to original data if backend fails
      if (originalData) {
        setCompanyData(originalData);
      }
      setLogoFile(null);
    }
  };

  return (
    <div className="logo-header-container">
      <h2>{dealerId ? `Dealer ${dealerName || ''} — Logo & Header` : 'Company Logo & Header Configuration'}</h2>

      <div className="logo-section">
        <h3>Company Logo</h3>
        <div className="logo-preview">
          <img src={logo} alt="Company Logo" />
        </div>
        <div className="logo-upload">
          <input
            type="file"
            id="logo-upload"
            accept="image/*"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
          />
          <label htmlFor="logo-upload" className="upload-button">
            Choose Logo
          </label>
          <span className="file-info">
            {logoFile ? logoFile.name : 'Current: assets/company_logo.png'}
          </span>
        </div>
      </div>

      <div className="company-details-section">
        <h3>Company Details</h3>
        <form className="company-form">
          <div className="form-group">
            <label htmlFor="name">Company Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={companyData.name}
              onChange={handleInputChange}
              placeholder="Enter company name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="details1">Address Line 1:</label>
            <input
              type="text"
              id="details1"
              name="details1"
              value={companyData.details1}
              onChange={handleInputChange}
              placeholder="Enter address line 1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="details2">Address Line 2:</label>
            <input
              type="text"
              id="details2"
              name="details2"
              value={companyData.details2}
              onChange={handleInputChange}
              placeholder="Enter address line 2"
            />
          </div>

          <div className="form-group">
            <label htmlFor="details3">Contact Information:</label>
            <input
              type="text"
              id="details3"
              name="details3"
              value={companyData.details3}
              onChange={handleInputChange}
              placeholder="Enter phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="timezone">Timezone:</label>
            <select
              id="timezone"
              name="timezone"
              value={companyData.timezone}
              onChange={handleInputChange}
            >
              {dealerId && (
                <option value="">Use owner's timezone</option>
              )}
              <option value="America/St_Johns">America/St_Johns — Newfoundland</option>
              <option value="America/Halifax">America/Halifax — Atlantic (Halifax)</option>
              <option value="America/Toronto">America/Toronto — Eastern (Toronto, Ottawa, Montreal)</option>
              <option value="America/Winnipeg">America/Winnipeg — Central (Winnipeg)</option>
              <option value="America/Regina">America/Regina — Saskatchewan (no DST)</option>
              <option value="America/Edmonton">America/Edmonton — Mountain (Calgary, Edmonton)</option>
              <option value="America/Vancouver">America/Vancouver — Pacific (Vancouver, Victoria)</option>
              <option value="America/New_York">America/New_York — US Eastern</option>
              <option value="America/Chicago">America/Chicago — US Central</option>
              <option value="America/Denver">America/Denver — US Mountain</option>
              <option value="America/Los_Angeles">America/Los_Angeles — US Pacific</option>
            </select>
          </div>
        </form>
      </div>

      <div className="actions">
        <button className="save-button" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        <button className="reset-button" onClick={handleReset} disabled={loading}>
          Reset
        </button>
      </div>
    </div>
  );
};

export default LogoHeader;
